/** Helpers compartidos: filtro de historial Training (semana / fechas). */

export function formatYmdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Lunes 00:00 local de la semana que contiene `base`, desplazada `weekOffset` semanas. */
export function startOfWeekMonday(base = new Date(), weekOffset = 0) {
  const x = new Date(base);
  x.setHours(12, 0, 0, 0);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day + weekOffset * 7);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfWeekSunday(weekStart) {
  const x = new Date(weekStart);
  x.setDate(x.getDate() + 6);
  return x;
}

export function formatWeekLabel(weekStart) {
  const end = endOfWeekSunday(weekStart);
  const fmt = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' });
  return `${fmt.format(weekStart)} – ${fmt.format(end)}`;
}

/**
 * @param {'week'|'range'|'all'} historyMode
 * @param {{ weekOffset?: number, rangeFrom?: string, rangeTo?: string }} opts
 */
export function resolveHistoryRange(historyMode, { weekOffset = 0, rangeFrom = '', rangeTo = '' } = {}) {
  if (historyMode === 'all') {
    return { from: null, to: null, label: 'Todo el historial' };
  }
  if (historyMode === 'week') {
    const start = startOfWeekMonday(new Date(), weekOffset);
    const end = endOfWeekSunday(start);
    return { from: formatYmdLocal(start), to: formatYmdLocal(end), label: formatWeekLabel(start) };
  }
  const from = rangeFrom || null;
  const to = rangeTo || null;
  if (!from && !to) return { from: null, to: null, label: 'Sin rango (todo el historial)' };
  return {
    from,
    to,
    label: `${from || '…'} → ${to || '…'}`,
  };
}

export function todayYmd() {
  return formatYmdLocal(new Date());
}

/** Últimos N días terminando hoy (inclusive). */
export function lastNDaysRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (Math.max(1, Number(days) || 1) - 1));
  return {
    from: formatYmdLocal(start),
    to: formatYmdLocal(end),
    label: `Últimos ${days} días`,
  };
}

/** Lunes de la semana ISO-local de una fecha YMD. */
export function mondayYmdOf(ymd) {
  const s = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return null;
  return formatYmdLocal(startOfWeekMonday(d, 0));
}

/**
 * Agrupa puntos diarios { fecha, weight_kg, reps, intensity, ... } por semana (lunes).
 */
export function groupEvolutionPointsByWeek(points) {
  const map = new Map();
  for (const pt of points || []) {
    const fecha = String(pt?.fecha || '').slice(0, 10);
    const weekStart = mondayYmdOf(fecha);
    if (!weekStart) continue;
    const prev = map.get(weekStart) || {
      fecha: weekStart,
      _weights: [],
      _maxWeights: [],
      _reps: [],
      _intensities: [],
      sessions: 0,
    };
    if (pt.weight_kg != null && Number.isFinite(Number(pt.weight_kg))) prev._weights.push(Number(pt.weight_kg));
    if (pt.max_weight_kg != null && Number.isFinite(Number(pt.max_weight_kg))) {
      prev._maxWeights.push(Number(pt.max_weight_kg));
    }
    if (pt.reps != null && Number.isFinite(Number(pt.reps))) prev._reps.push(Number(pt.reps));
    if (pt.intensity != null && Number.isFinite(Number(pt.intensity))) {
      prev._intensities.push(Number(pt.intensity));
    }
    prev.sessions += Number(pt.sessions) || 1;
    map.set(weekStart, prev);
  }
  const avg = (vals) => {
    if (!vals.length) return null;
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
  };
  return [...map.values()]
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((row) => {
      const start = new Date(`${row.fecha}T12:00:00`);
      return {
        fecha: row.fecha,
        label: formatWeekLabel(start),
        weight_kg: avg(row._weights),
        max_weight_kg: row._maxWeights.length ? Math.max(...row._maxWeights) : null,
        reps: avg(row._reps),
        intensity: avg(row._intensities),
        sessions: row.sessions,
      };
    });
}
